import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Spinner, Form, Button, Modal, Badge, Pagination } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import axios from '../api/axios';
import Header from '../layouts/Header';

/* ─── Formatters ─── */
const fmt = (val, dec = 2) =>
    `₹${Number(val || 0).toLocaleString('en-IN', { minimumFractionDigits: dec, maximumFractionDigits: dec })}`;
const fmtNum = (val) => Number(val || 0).toLocaleString('en-IN');
const fmtTime = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
};
const fmtDateLabel = (df, cr) => {
    if (df === 'custom' && cr) return `${cr.start} → ${cr.end}`;
    return { today: 'Today', yesterday: 'Yesterday', last7days: 'Last 7 Days', last30days: 'Last 30 Days', thisMonth: 'This Month', lastMonth: 'Last Month' }[df] || df;
};
const fmtViewDate = (d) =>
    new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
const fmtViewTime = (d) =>
    new Date(d).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });

/* ─── Date filter → { startDate, endDate } strings ─── */
const resolveDateRange = (dateFilter, appliedCustomRange) => {
    const pad = (n) => String(n).padStart(2, '0');
    const toStr = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    switch (dateFilter) {
        case 'today': {
            const s = toStr(today);
            return { startDate: s, endDate: s };
        }
        case 'yesterday': {
            const y = new Date(today);
            y.setDate(y.getDate() - 1);
            const s = toStr(y);
            return { startDate: s, endDate: s };
        }
        case 'last7days': {
            const s = new Date(today);
            s.setDate(s.getDate() - 6);
            return { startDate: toStr(s), endDate: toStr(today) };
        }
        case 'last30days': {
            const s = new Date(today);
            s.setDate(s.getDate() - 29);
            return { startDate: toStr(s), endDate: toStr(today) };
        }
        case 'thisMonth': {
            const s = new Date(today.getFullYear(), today.getMonth(), 1);
            return { startDate: toStr(s), endDate: toStr(today) };
        }
        case 'lastMonth': {
            const s = new Date(today.getFullYear(), today.getMonth() - 1, 1);
            const e = new Date(today.getFullYear(), today.getMonth(), 0);
            return { startDate: toStr(s), endDate: toStr(e) };
        }
        case 'custom': {
            if (appliedCustomRange) {
                return { startDate: appliedCustomRange.start, endDate: appliedCustomRange.end };
            }
            const s = toStr(today);
            return { startDate: s, endDate: s };
        }
        default: {
            const s = toStr(today);
            return { startDate: s, endDate: s };
        }
    }
};

/* ─── Modal filter configs ─── */
const MODAL_CONFIGS = {
    total: { title: 'Total Orders', badge: 'primary', statusFilter: '', typeFilter: '' },
    cancelled: { title: 'Cancelled Orders', badge: 'danger', statusFilter: 'CANCELLED', typeFilter: '' },
    delayed: { title: 'Delayed Orders', badge: 'warning', statusFilter: '', typeFilter: '' },
    complimentary: { title: 'Complimentary Orders', badge: 'secondary', statusFilter: '', typeFilter: 'COMPLIMENTARY' },
};

const DATE_FILTER_OPTIONS = [
    { label: 'Today', value: 'today' },
    { label: 'Yesterday', value: 'yesterday' },
    { label: 'Last 7 Days', value: 'last7days' },
    { label: 'Last 30 Days', value: 'last30days' },
    { label: 'This Month', value: 'thisMonth' },
    { label: 'Last Month', value: 'lastMonth' },
    { label: 'Custom Range', value: 'custom' },
];

const getDefaultStartDate = () => {
    const d = new Date();
    d.setDate(d.getDate() - 91);
    return d;
};

/* ─── Sub-components ─── */
const KpiPill = ({ label, value, icon, color, bg, sub, onClick }) => (
    <div
        onClick={onClick}
        style={{
            background: bg,
            borderRadius: 10,
            padding: '10px 13px',
            borderLeft: `3px solid ${color}`,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            cursor: onClick ? 'pointer' : 'default',
            transition: 'transform 0.12s, box-shadow 0.12s',
        }}
        onMouseEnter={e => { if (onClick) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.10)'; } }}
        onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
        title={onClick ? `Click to view ${label}` : undefined}
    >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 9, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {onClick && <i className="bi bi-box-arrow-up-right" style={{ fontSize: 9, color, opacity: 0.6 }} />}
                <i className={`bi ${icon}`} style={{ fontSize: 12, color, opacity: 0.75 }} />
            </div>
        </div>
        <div style={{ fontSize: 15, fontWeight: 800, color: '#111827', letterSpacing: '-0.4px', lineHeight: 1.1 }}>{value}</div>
        {sub && <div style={{ fontSize: 9, color: '#9ca3af', marginTop: 1 }}>{sub}</div>}
    </div>
);

const Card = ({ title, icon, children, style = {} }) => (
    <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'visible', ...style }}>
        {title && (
            <div style={{ padding: '9px 14px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: 7, background: '#fafafa' }}>
                <i className={`bi ${icon}`} style={{ fontSize: 11, color: '#4d8af0' }} />
                <span style={{ fontSize: 10, fontWeight: 800, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.7px' }}>{title}</span>
            </div>
        )}
        <div style={{ padding: '11px 13px' }}>{children}</div>
    </div>
);

const RankRow = ({ rank, name, qty, isTop }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px', borderRadius: 7, marginBottom: 3, background: rank === 1 ? (isTop ? '#f0fdf4' : '#fff5f5') : 'transparent' }}>
        <div style={{ width: 18, height: 18, borderRadius: '50%', fontSize: 9, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, background: rank === 1 ? (isTop ? '#22c55e' : '#ef4444') : '#e5e7eb', color: rank === 1 ? '#fff' : '#9ca3af' }}>{rank}</div>
        <span style={{ flex: 1, fontSize: 11, color: '#374151', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {!name || name === 'N/A' ? <span style={{ color: '#d1d5db' }}>—</span> : name}
        </span>
        <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 99, flexShrink: 0, color: isTop ? '#16a34a' : '#dc2626', background: isTop ? '#dcfce7' : '#fee2e2' }}>{qty}</span>
    </div>
);

const HourlyBar = ({ slot, orders, sales, maxOrders }) => {
    const pct = maxOrders > 0 ? (orders / maxOrders) * 100 : 0;
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7 }}>
            <span style={{ width: 110, fontSize: 10, color: '#6b7280', flexShrink: 0 }}>{slot}</span>
            <div style={{ flex: 1, height: 6, background: '#f3f4f6', borderRadius: 99, overflow: 'hidden' }}>
                <div style={{ width: `${pct}%`, height: '100%', background: 'linear-gradient(90deg,#4d8af0,#6fa3f7)', borderRadius: 99 }} />
            </div>
            <span style={{ width: 52, fontSize: 10, fontWeight: 600, color: '#374151', textAlign: 'right', flexShrink: 0 }}>{orders} orders</span>
            <span style={{ width: 72, fontSize: 10, fontWeight: 600, color: '#059669', textAlign: 'right', flexShrink: 0 }}>{fmt(sales)}</span>
        </div>
    );
};

const CouponRow = ({ coupon, used, discount }) => (
    <div style={{ display: 'flex', alignItems: 'center', padding: '5px 0', borderBottom: '1px solid #f9fafb' }}>
        <i className="bi bi-tag-fill" style={{ fontSize: 10, color: '#8b5cf6', marginRight: 7, flexShrink: 0 }} />
        <span style={{ flex: 1, fontSize: 11, fontWeight: 700, color: '#374151', fontFamily: 'monospace' }}>{coupon}</span>
        <span style={{ fontSize: 10, color: '#6b7280', marginRight: 10 }}>{used}x used</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#dc2626' }}>-{fmt(discount)}</span>
    </div>
);

/* ─── Status badge colour helper ─── */
const getStatusBadge = (status) => {
    const map = {
        COMPLETED: 'success',
        DELIVERED: 'primary',
        CONFIRMED: 'secondary',
        'READY-TO-PICK': 'warning',
        CANCELLED: 'danger',
    };
    return map[status] || 'dark';
};

/* ─── Orders Modal ─── */
const OrdersModal = ({ show, onHide, modalType, outletId, dateFilter, appliedCustomRange, outletName }) => {
    const token = localStorage.getItem('authToken');
    const config = MODAL_CONFIGS[modalType] || MODAL_CONFIGS.total;

    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const LIMIT = 25;

    const fetchOrders = useCallback(async (page = 1) => {
        if (!outletId) return;
        try {
            setLoading(true);
            const { startDate, endDate } = resolveDateRange(dateFilter, appliedCustomRange);
            const params = { startDate, endDate, page: String(page), limit: String(LIMIT) };
            if (config.statusFilter) params.orderStatus = config.statusFilter;
            if (config.typeFilter) params.orderType = config.typeFilter;

            // Special case: delayed orders — no dedicated API param, filter client-side
            // If API supports orderArrivalStatus in future, add it here

            const res = await axios({
                method: 'POST',
                url: 'order/getOrdersForAdmin/new',
                params,
                data: { outletIds: [outletId] },
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            });

            const d = res?.data?.data;
            if (d?.title === 'Success') {
                let fetched = d.orders || [];

                // Client-side filter for delayed (no dedicated API param)
                if (modalType === 'delayed') {
                    fetched = fetched.filter(o => o.orderArrivalStatus === 'DELAYED');
                }

                const sorted = [...fetched].sort(
                    (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
                );
                setOrders(sorted);
                setTotalPages(d.paginationData?.totalPages || 1);
                setTotalCount(d.paginationData?.totalRecords || fetched.length);
                setCurrentPage(d.paginationData?.currentPage || page);
            } else {
                setOrders([]);
            }
        } catch (err) {
            console.error('Orders modal fetch error:', err);
            setOrders([]);
        } finally {
            setLoading(false);
        }
    }, [outletId, dateFilter, appliedCustomRange, modalType, config, token]);

    useEffect(() => {
        if (show) {
            setCurrentPage(1);
            fetchOrders(1);
        } else {
            setOrders([]);
        }
    }, [show, fetchOrders]);

    const handlePageChange = (page) => {
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
        fetchOrders(page);
    };

    const { startDate, endDate } = resolveDateRange(dateFilter, appliedCustomRange);
    const dateLabel = startDate === endDate ? startDate : `${startDate} → ${endDate}`;

    /* ── Pagination items ── */
    const renderPages = () => {
        if (totalPages <= 1) return null;
        const items = [];
        const max = Math.min(totalPages, 7);
        let start = Math.max(1, currentPage - 3);
        let end = Math.min(totalPages, start + max - 1);
        if (end - start < max - 1) start = Math.max(1, end - max + 1);

        if (start > 1) {
            items.push(<Pagination.Item key={1} onClick={() => handlePageChange(1)}>1</Pagination.Item>);
            if (start > 2) items.push(<Pagination.Ellipsis key="e1" />);
        }
        for (let i = start; i <= end; i++) {
            items.push(
                <Pagination.Item key={i} active={i === currentPage} onClick={() => handlePageChange(i)}>{i}</Pagination.Item>
            );
        }
        if (end < totalPages) {
            if (end < totalPages - 1) items.push(<Pagination.Ellipsis key="e2" />);
            items.push(<Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>{totalPages}</Pagination.Item>);
        }
        return items;
    };

    return (
        <Modal show={show} onHide={onHide} size="xl" centered scrollable>
            <Modal.Header closeButton style={{ borderBottom: '1px solid #e5e7eb', padding: '14px 20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{
                        width: 36, height: 36, borderRadius: 9,
                        background: config.badge === 'danger' ? '#fee2e2' : config.badge === 'warning' ? '#fef3c7' : config.badge === 'primary' ? '#eff6ff' : '#f3f4f6',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <i className={
                            config.badge === 'danger' ? 'bi bi-x-circle-fill' :
                                config.badge === 'warning' ? 'bi bi-clock-history' :
                                    config.badge === 'secondary' ? 'bi bi-gift-fill' :
                                        'bi bi-bag-fill'
                        } style={{
                            color: config.badge === 'danger' ? '#dc2626' : config.badge === 'warning' ? '#d97706' : config.badge === 'primary' ? '#2563eb' : '#6b7280',
                            fontSize: 16,
                        }} />
                    </div>
                    <div>
                        <div style={{ fontSize: 16, fontWeight: 800, color: '#111827' }}>{config.title}</div>
                        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 1 }}>
                            <i className="bi bi-shop" style={{ fontSize: 10, marginRight: 4 }} />{outletName}
                            <span style={{ margin: '0 6px', color: '#d1d5db' }}>·</span>
                            <i className="bi bi-calendar3" style={{ fontSize: 10, marginRight: 4 }} />{dateLabel}
                            {totalCount > 0 && (
                                <>
                                    <span style={{ margin: '0 6px', color: '#d1d5db' }}>·</span>
                                    <strong style={{ color: '#374151' }}>{fmtNum(totalCount)}</strong> records
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </Modal.Header>

            <Modal.Body style={{ padding: 0 }}>
                {loading ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 0', gap: 12 }}>
                        <Spinner animation="border" variant="primary" />
                        <span style={{ fontSize: 13, color: '#6b7280' }}>Loading orders…</span>
                    </div>
                ) : orders.length === 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 0', gap: 10 }}>
                        <i className="bi bi-inbox" style={{ fontSize: 40, color: '#d1d5db' }} />
                        <div style={{ fontSize: 14, fontWeight: 700, color: '#374151' }}>No orders found</div>
                        <div style={{ fontSize: 12, color: '#9ca3af' }}>Try adjusting the date range</div>
                    </div>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                            <thead>
                                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
                                    {['Sr No', 'Date / Time', 'Order No', 'Customer', 'Status', 'Taxable', 'Tax', 'Discount', 'Total'].map((h, i) => (
                                        <th key={h} style={{
                                            padding: '10px 14px',
                                            fontSize: 10, fontWeight: 700, color: '#6b7280',
                                            textTransform: 'uppercase', letterSpacing: '0.6px',
                                            whiteSpace: 'nowrap',
                                            textAlign: i >= 7 ? 'right' : 'left',
                                        }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {orders?.map((order, idx) => {
                                    const name = order.user?.name || order.outletUser?.name || order.kioskUser?.name || 'N/A';
                                    const mobile = order.user?.mobile || order.outletUser?.mobile || order.kioskUser?.mobile || '';
                                    const orderNo = `${order?.order_sequence || ''}${order.order_no || ''}`;
                                    const isDelayed = order?.orderArrivalStatus === 'DELAYED';

                                    return (
                                        <tr key={order._id || idx} style={{ borderBottom: '1px solid #f0f2f5', transition: 'background 0.1s' }}
                                            onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                                            onMouseLeave={e => e.currentTarget.style.background = ''}
                                        >
                                            {/* Sr */}
                                            <td style={{ padding: '10px 14px', color: '#9ca3af', fontSize: 12 }}>
                                                {(currentPage - 1) * LIMIT + idx + 1}
                                            </td>

                                            {/* Date / Time */}
                                            <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                                                <div style={{ fontSize: 12.5, fontWeight: 500, color: '#1a202c' }}>{fmtViewDate(order.createdAt)}</div>
                                                <div style={{ fontSize: 11, color: '#9ca3af' }}>{fmtViewTime(order.createdAt)}</div>
                                            </td>

                                            {/* Order No */}
                                            <td style={{ padding: '10px 14px' }}>
                                                <span style={{ fontSize: 12, fontWeight: 500, color: '#1a202c', background: '#f0f2f5', padding: '2px 7px', borderRadius: 4, whiteSpace: 'nowrap' }}>
                                                    {orderNo}
                                                </span>
                                            </td>

                                            {/* Customer */}
                                            <td style={{ padding: '10px 14px', minWidth: 140 }}>
                                                <div style={{ fontWeight: 600, fontSize: 13, color: '#1a202c' }}>{name}</div>
                                                {mobile && <div style={{ fontSize: 11, color: '#2563eb' }}>{mobile}</div>}
                                                {order.kioskUser && (
                                                    <span style={pillStyle('#eff6ff', '#1d4ed8')}>Kiosk</span>
                                                )}
                                                {order.subscriptionId && (
                                                    <span style={pillStyle('#f0fdf4', '#15803d')}>Subscription</span>
                                                )}
                                                {(order.paymentMode === 'COMPLIMENTARY' || order.grandTotal === 0) && !order.subscriptionId && (
                                                    <span style={pillStyle('#fdf4ff', '#7e22ce')}>Complimentary</span>
                                                )}
                                                {order?.outletUser && order.source === 'POS' && order.paymentMode !== 'COMPLIMENTARY' && !order.subscriptionId && (
                                                    <span style={pillStyle('#fdf4ff', '#ce2295')}>POS</span>
                                                )}
                                            </td>

                                            {/* Type */}
                                            {/* <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                                                <span style={{ fontSize: 11.5, color: '#374151' }}>{order.ordersType || order.source || '—'}</span>
                                            </td> */}

                                            {/* Status */}
                                            <td style={{ padding: '10px 14px' }}>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                    <Badge bg={getStatusBadge(order.orderStatus)} style={{ fontSize: 10, fontWeight: 700 }}>
                                                        {order.orderStatus}
                                                    </Badge>
                                                    {/* Arrival tag */}
                                                    {order.orderArrivalStatus === 'DELAYED' && (
                                                        <span style={{ ...arrivalStyle('#fef2f2', '#dc2626'), fontSize: 10 }}>
                                                            ⏱ +{order.delayedArrival}m late
                                                        </span>
                                                    )}
                                                    {order.orderArrivalStatus === 'EARLY' && (
                                                        <span style={{ ...arrivalStyle('#f0fdf4', '#16a34a'), fontSize: 10 }}>
                                                            ⚡ -{order.earlyArrival}m early
                                                        </span>
                                                    )}
                                                </div>
                                            </td>

                                            {/* Payment */}
                                            {/* <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                                                <div style={{ fontSize: 11.5, color: '#374151' }}>{order.paymentMode || '—'}</div>
                                                <Badge bg={order.paymentStatus === 'PAID' ? 'success' : 'danger'} style={{ fontSize: 9, marginTop: 2 }}>
                                                    {order.paymentStatus || 'N/A'}
                                                </Badge>
                                            </td> */}

                                            {/* Taxable */}
                                            <td style={{ padding: '10px 14px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                <span style={{ fontSize: 12.5, fontWeight: 500, color: '#059669' }}>₹{order.totalTaxable?.toFixed(2)}</span>
                                            </td>

                                            {/* Tax */}
                                            <td style={{ padding: '10px 14px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                <span style={{ fontSize: 12.5, fontWeight: 500, color: '#d97706' }}>₹{order.totalTaxAmount?.toFixed(2)}</span>
                                            </td>

                                            {/* Discount */}
                                            <td style={{ padding: '10px 14px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                <span style={{ fontSize: 12.5, fontWeight: 500, color: '#dc2626' }}>₹{order.totalDiscount?.toFixed(2)}</span>
                                            </td>

                                            {/* Grand Total */}
                                            <td style={{ padding: '10px 14px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                <span style={{ fontSize: 13, fontWeight: 700, color: '#2563eb' }}>₹{order.grandTotal?.toFixed(2)}</span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </Modal.Body>

            {/* Pagination footer */}
            {!loading && totalPages > 1 && (
                <Modal.Footer style={{ borderTop: '1px solid #e5e7eb', padding: '10px 20px', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 12, color: '#6b7280' }}>
                        Page {currentPage} of {totalPages} · {fmtNum(totalCount)} total orders
                    </span>
                    <Pagination className="mb-0" size="sm">
                        <Pagination.Prev disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)} />
                        {renderPages()}
                        <Pagination.Next disabled={currentPage === totalPages} onClick={() => handlePageChange(currentPage + 1)} />
                    </Pagination>
                </Modal.Footer>
            )}
        </Modal>
    );
};

/* ─── Tiny style helpers ─── */
const pillStyle = (bg, color) => ({
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '2px 7px', borderRadius: 20,
    fontSize: 10, fontWeight: 700,
    background: bg, color, marginTop: 3,
    border: `1px solid ${color}22`,
});
const arrivalStyle = (bg, color) => ({
    display: 'inline-flex', alignItems: 'center',
    padding: '2px 6px', borderRadius: 12,
    fontWeight: 700,
    background: bg, color,
});

/* ─── Stock alert box ─── */
const StockAlertBox = ({ count, label, bg, color, products }) => {
    const [hover, setHover] = useState(false);
    return (
        <div
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            style={{ position: 'relative', textAlign: 'center', padding: '10px 4px', background: bg, borderRadius: 8, cursor: products?.length ? 'pointer' : 'default' }}
        >
            <div style={{ fontSize: 22, fontWeight: 800, color }}>{fmtNum(count)}</div>
            <div style={{ fontSize: 9, color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase', marginTop: 2 }}>{label}</div>
            {hover && products?.length > 0 && (
                <div style={{
                    position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)',
                    marginBottom: 8, background: '#1f2937', color: '#f9fafb', borderRadius: 9,
                    padding: '8px 4px', minWidth: 175, boxShadow: '0 8px 20px rgba(0,0,0,0.28)',
                    zIndex: 999999, border: '1px solid rgba(255,255,255,0.08)',
                }}>
                    {products.map((p, i) => (
                        <div key={p.productId || i} style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                            padding: '4px 10px', fontSize: 11.5, fontWeight: 500,
                            borderBottom: i < products.length - 1 ? '1px solid rgba(255,255,255,0.07)' : 'none',
                        }}>
                            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.productName}</span>
                            <span style={{ fontSize: 10, fontWeight: 700, color, background: 'rgba(255,255,255,0.1)', borderRadius: 99, padding: '1px 7px', flexShrink: 0 }}>
                                × {p.exhaustedCount ?? p.damagedCount}
                            </span>
                        </div>
                    ))}
                    <div style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', width: 0, height: 0, borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop: '6px solid #1f2937' }} />
                </div>
            )}
        </div>
    );
};

/* ════════════════════════════════════════════
   MAIN COMPONENT
════════════════════════════════════════════ */
const OutletDetailScreen = () => {
    const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : '';
    const [skin, setSkin] = useState(currentSkin);
    const { outletId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const token = localStorage.getItem('authToken');

    const {
        outletName = 'Outlet Details',
        dateFilter: initialDateFilter = 'today',
        customRange: initialCustomRange = null,
    } = location.state || {};

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [dateFilter, setDateFilter] = useState(initialDateFilter);
    const [appliedCustomRange, setAppliedCustomRange] = useState(initialCustomRange);
    const [customStartDate, setCustomStartDate] = useState(
        initialCustomRange ? new Date(initialCustomRange.start) : getDefaultStartDate
    );
    const [customEndDate, setCustomEndDate] = useState(
        initialCustomRange ? new Date(initialCustomRange.end) : () => new Date()
    );
    const [customDateError, setCustomDateError] = useState('');
        const [showPdfTooltip, setShowPdfTooltip] = useState(false);

    /* ── Orders modal state ── */
    const [ordersModal, setOrdersModal] = useState({ show: false, type: null });
    const openOrdersModal = (type) => setOrdersModal({ show: true, type });
    const closeOrdersModal = () => setOrdersModal({ show: false, type: null });

    const fetchDetails = useCallback(async () => {
        try {
            setLoading(true); setError(null);
            let url = `/dashboard/getRevenueStatThroughSummaryByOutlet?outletId=${outletId}`;
            url += dateFilter === 'custom' && appliedCustomRange
                ? `&filterType=custom&startDate=${appliedCustomRange.start}&endDate=${appliedCustomRange.end}`
                : `&filterType=${dateFilter}`;
            const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
            setData(res?.data?.data || null);
        } catch (err) {
            console.error(err);
            setError('Failed to load outlet details.');
        } finally {
            setLoading(false);
        }
    }, [outletId, dateFilter, appliedCustomRange, token]);

    const formatMinutes = (mins = 0) => {
        const hrs = Math.floor(mins / 60);
        const rem = mins % 60;

        if (hrs === 0) return `${rem} Min`;
        if (rem === 0) return `${hrs} Hr`;

        return `${hrs} Hr ${rem} Min`;
    };

    useEffect(() => { fetchDetails(); }, [fetchDetails]);

    const handleDateFilterChange = (val) => {
        setDateFilter(val);
        setCustomDateError('');
        if (val === 'custom') {
            const start = getDefaultStartDate();
            const end = new Date();
            setCustomStartDate(start);
            setCustomEndDate(end);
            setAppliedCustomRange({
                start: start.toISOString().split('T')[0],
                end: end.toISOString().split('T')[0],
            });
        } else {
            setAppliedCustomRange(null);
            setCustomStartDate(null);
            setCustomEndDate(null);
        }
    };

    const handleApplyCustomRange = () => {
        if (!customStartDate || !customEndDate) {
            setCustomDateError('Please select both dates.');
            return;
        }
        const diffDays = Math.ceil((customEndDate - customStartDate) / (1000 * 60 * 60 * 24));
        if (diffDays > 92) {
            setCustomDateError('Range cannot exceed 92 days.');
            return;
        }
        setCustomDateError('');
        setAppliedCustomRange({
            start: customStartDate.toISOString().split('T')[0],
            end: customEndDate.toISOString().split('T')[0],
        });
    };

    const maxOrders = useMemo(() =>
        Math.max(...(data?.hourlyDistribution || []).map(h => h.totalOrders || 0), 1),
        [data]);

    useEffect(() => {
        const s = document.createElement('style');
        s.id = 'outlet-print-style';
        s.innerHTML = `@media print { body > *:not(#outlet-detail-root) { display:none!important; } #outlet-detail-root { display:block!important; background:#fff!important; } .no-print { display:none!important; } @page { margin:15mm; } }`;
        document.head.appendChild(s);
        return () => document.getElementById('outlet-print-style')?.remove();
    }, []);

    const { stats, hourlyDistribution, topProducts, bottomProducts,
        topCoupons, totalOnlineSales, outletShiftSummary,
        pettyCashSummary, damagedProductsSummary, exhaustedStockSummary, userStatsSummary } = data || {};

    const netOrders = (Number(stats?.totalOrders) || 0) - (Number(stats?.cancelledOrders) || 0);

    return (
        <React.Fragment>
            <Header onSkin={setSkin} />
            <div id="outlet-detail-root" className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>

                {/* Top Bar */}
                <div className="no-print" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                        <button onClick={() => navigate(-1)}
                            style={{ display: 'flex', alignItems: 'center', gap: 6, border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', padding: '7px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151' }}
                            onMouseEnter={e => e.currentTarget.style.background = '#f3f4f6'}
                            onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                        >
                            <i className="bi bi-arrow-left" /> Back
                        </button>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ width: 36, height: 36, borderRadius: 9, background: '#4d8af0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <i className="bi bi-shop" style={{ fontSize: 16, color: '#fff' }} />
                            </div>
                            <div>
                                <div style={{ fontSize: 16, fontWeight: 800, color: '#111827' }}>{outletName}</div>
                                <div style={{ fontSize: 11, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <i className="bi bi-calendar3" style={{ fontSize: 10 }} /> {fmtDateLabel(dateFilter, appliedCustomRange)}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className='d-flex align-items-center gap-2'>
                        <div className="d-flex align-items-center bg-light p-1 ps-2 rounded" style={{ flexWrap: 'wrap', gap: 8, border: '0.5px solid rgb(77, 138, 240)' }}>
                            <i className="bi bi-calendar-event text-primary" style={{ fontSize: 12 }} />
                            <Form.Select
                                value={dateFilter}
                                onChange={(e) => handleDateFilterChange(e.target.value)}
                                size="sm"
                                style={{ width: 145 }}
                                className="border-0 bg-light"
                            >
                                {DATE_FILTER_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </Form.Select>
                            {['last7days', 'last30days', 'thisMonth', 'lastMonth'].includes(dateFilter) && (
                                <span style={{ fontSize: 10, color: '#888', width: '100%', paddingLeft: 2 }}>
                                    * Today's data not included
                                </span>
                            )}

                            {dateFilter === 'custom' && (
                                <div className="d-flex align-items-center gap-2">
                                    <div className="d-flex flex-column">
                                        <span style={{ fontSize: 10, color: '#3f3f3f', marginBottom: 2 }}>Start date</span>
                                        <DatePicker
                                            selected={customStartDate}
                                            onChange={(date) => {
                                                setCustomStartDate(date);
                                                if (customEndDate) {
                                                    const diff = Math.ceil((customEndDate - date) / (1000 * 60 * 60 * 24));
                                                    if (diff > 92 || diff < 0) setCustomEndDate(null);
                                                }
                                                setCustomDateError('');
                                            }}
                                            selectsStart startDate={customStartDate} endDate={customEndDate}
                                            minDate={new Date(new Date().setDate(new Date().getDate() - 92))}
                                            maxDate={new Date()} dateFormat="dd-MM-yyyy" placeholderText="Start date"
                                            className="form-control form-control-sm" wrapperClassName="date-picker-wrapper"
                                        />
                                    </div>
                                    <span style={{ fontSize: 13, color: '#888', marginTop: 14 }}>→</span>
                                    <div className="d-flex flex-column">
                                        <span style={{ fontSize: 10, color: '#3f3f3f', marginBottom: 2 }}>End date</span>
                                        <DatePicker
                                            selected={customEndDate}
                                            onChange={(date) => { setCustomEndDate(date); setCustomDateError(''); }}
                                            selectsEnd startDate={customStartDate} endDate={customEndDate}
                                            minDate={customStartDate || new Date(new Date().setDate(new Date().getDate() - 90))}
                                            maxDate={customStartDate ? new Date(Math.min(new Date(customStartDate).setDate(customStartDate.getDate() + 92), new Date())) : new Date()}
                                            disabled={!customStartDate} dateFormat="dd-MM-yyyy" placeholderText="End date"
                                            className="form-control form-control-sm" wrapperClassName="date-picker-wrapper"
                                        />
                                    </div>
                                    <div className="d-flex flex-column">
                                        <span style={{ fontSize: 10, color: 'transparent', marginBottom: 2 }}>-</span>
                                        <Button size="sm" variant="primary" onClick={handleApplyCustomRange} disabled={!customStartDate || !customEndDate}>
                                            Apply
                                        </Button>
                                    </div>
                                </div>
                            )}
                            {dateFilter === 'custom' && customDateError && (
                                <span style={{ fontSize: 11, color: '#dc3545', width: '100%', paddingLeft: 2 }}>⚠ {customDateError}</span>
                            )}
                        </div>

                        {/* Download PDF — in development, show tooltip on hover */}
                        <div style={{ position: 'relative', display: 'inline-block' }}
                            onMouseEnter={() => setShowPdfTooltip(true)}
                            onMouseLeave={() => setShowPdfTooltip(false)}
                        >
                            <button
                                onClick={() => { }}
                                style={{ display: 'flex', alignItems: 'center', gap: 7, background: '#9ca3af', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px', cursor: 'not-allowed', fontSize: 13, fontWeight: 600, boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }}
                            >
                                <i className="bi bi-file-earmark-pdf-fill" style={{ fontSize: 14 }} /> Download PDF
                            </button>
                            {showPdfTooltip && (
                                <div style={{
                                    position: 'absolute', bottom: '100%', right: 0, marginBottom: 8,
                                    background: '#1f2937', color: '#f9fafb', borderRadius: 8,
                                    padding: '7px 12px', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
                                    boxShadow: '0 8px 20px rgba(0,0,0,0.28)', zIndex: 999999,
                                }}>
                                    In Development process, Will be Available soon
                                    <div style={{ position: 'absolute', top: '100%', right: 14, width: 0, height: 0, borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop: '6px solid #1f2937' }} />
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {loading && (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 340, gap: 12 }}>
                        <Spinner animation="border" variant="primary" />
                        <span style={{ fontSize: 13, color: '#6b7280' }}>Loading outlet details...</span>
                    </div>
                )}
                {error && (
                    <div style={{ textAlign: 'center', padding: '80px 0' }}>
                        <i className="bi bi-exclamation-circle" style={{ fontSize: 36, color: '#ef4444', display: 'block', marginBottom: 10 }} />
                        <div style={{ fontSize: 13, color: '#374151', marginBottom: 14 }}>{error}</div>
                        <button onClick={fetchDetails} style={{ padding: '7px 18px', borderRadius: 8, border: 'none', background: '#4d8af0', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Retry</button>
                    </div>
                )}

                {!loading && !error && data && (
                    <>
                        {/* Hero Strip */}
                        <div style={{
                            background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5fa3 60%, #4d8af0 100%)',
                            borderRadius: 14, padding: '14px 4px', marginBottom: 10,
                            display: 'grid', gridTemplateColumns: 'repeat(6,1fr)',
                            position: 'relative', overflow: 'hidden',
                        }}>
                            <div style={{ position: 'absolute', right: -40, top: -40, width: 180, height: 180, borderRadius: '50%', background: 'rgba(255,255,255,0.04)', pointerEvents: 'none' }} />
                            {[
                                { label: 'Total Sales', value: fmt(stats?.totalSales), icon: 'bi-currency-rupee' },
                                { label: 'Net Sales', value: fmt(stats?.netSales), icon: 'bi-receipt' },
                                { label: 'App Net Sale', value: fmt(stats?.appNetSale), icon: 'bi-phone' },
                                { label: 'Kiosk Net Sale', value: fmt(stats?.kioskNetSale), icon: 'bi-display' },
                                { label: 'Net Orders', value: fmtNum(netOrders), icon: 'bi-bag-check' },
                                { label: 'Total Online', value: fmt(totalOnlineSales?.totalRevenue), icon: 'bi-globe' },
                            ].map((item, i) => (
                                <div key={i} style={{ padding: '6px 16px', borderRight: i < 5 ? '1px solid rgba(255,255,255,0.1)' : 'none' }}>
                                    <div style={{ fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 5 }}>
                                        <i className={`bi ${item.icon}`} style={{ fontSize: 10 }} /> {item.label}
                                    </div>
                                    <div style={{ fontSize: 17, fontWeight: 800, color: '#fff', letterSpacing: '-0.5px' }}>{item.value}</div>
                                </div>
                            ))}
                        </div>

                        {/* ── KPI Pills — clickable ones open modal ── */}
                        <div className='mt-3' style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 8, marginBottom: 10 }}>
                            <KpiPill
                                label="Total Orders" value={fmtNum(stats?.totalOrders)}
                                icon="bi-bag" color="#0ea5e9" bg="#f0f9ff"
                                onClick={() => openOrdersModal('total')}
                            />
                            <KpiPill
                                label="Cancelled" value={fmtNum(stats?.cancelledOrders)}
                                sub={stats?.cancelledOrderAmount ? `${fmt(stats.cancelledOrderAmount)} lost` : 'No cancellations'}
                                icon="bi-x-circle" color="#ef4444" bg="#fff5f5"
                                onClick={() => openOrdersModal('cancelled')}
                            />
                            <KpiPill
                                label="Delayed" value={fmtNum(stats?.delayedOrders)}
                                icon="bi-clock-history" color="#f59e0b" bg="#fffbeb"
                                onClick={() => openOrdersModal('delayed')}
                            />
                            <KpiPill
                                label="Early" value={fmtNum(stats?.earlyOrders)}
                                icon="bi-lightning-charge" color="#22c55e" bg="#f0fdf4"
                            />
                            <KpiPill
                                label="Complimentary" value={fmtNum(stats?.complimentaryOrders)}
                                icon="bi-gift" color="#8b5cf6" bg="#f5f3ff"
                                onClick={() => openOrdersModal('complimentary')}
                            />
                            {/* <KpiPill
                                label="Shift Open" value={fmtTime(outletShiftSummary?.openTime)}
                                sub={outletShiftSummary?.closeTime ? `Closed ${fmtTime(outletShiftSummary.closeTime)}` : 'Still open'}
                                icon="bi-door-open" color="#0ea5e9" bg="#f0f9ff"
                            /> */}
                            <KpiPill
                                label="Outlet Closures"
                                value={`${outletShiftSummary?.outletClosedTotalTimes || 0} ${outletShiftSummary?.outletClosedTotalTimes === 1 ? 'Time' : 'Times'}`}
                                sub={`Closed for ${formatMinutes(outletShiftSummary?.outletClosedTotalDurationInMinutes)}`}
                                icon="bi-door-closed"
                                color="#ef4444"
                                bg="#fef2f2"
                            />
                            <KpiPill
                                label="New Users" value={fmtNum(userStatsSummary?.newUserJoinedCount)}
                                sub={`${fmtNum(userStatsSummary?.repeatedUserCount)} returning`}
                                icon="bi-people-fill" color="#8b5cf6" bg="#f5f3ff"
                            />
                        </div>

                        {/* Bento Grid — unchanged */}
                        <div className="bento-grid mt-3" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, alignItems: 'start' }}>

                            {/* Col A */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                <Card title="Online Platform Sales" icon="bi-globe2">
                                    {[
                                        { label: 'Swiggy', icon: 'bi-bag-heart', color: '#ff6900', bg: '#fff4ed', rev: totalOnlineSales?.swiggy?.revenue, orders: totalOnlineSales?.swiggy?.totalOrders },
                                        { label: 'Zomato', icon: 'bi-fire', color: '#e23744', bg: '#fff5f5', rev: totalOnlineSales?.zomato?.revenue, orders: totalOnlineSales?.zomato?.totalOrders },
                                    ].map(p => (
                                        <div key={p.label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', borderRadius: 8, background: p.bg, marginBottom: 6 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <i className={`bi ${p.icon}`} style={{ fontSize: 14, color: p.color }} />
                                                <div>
                                                    <div style={{ fontSize: 11, fontWeight: 700, color: '#374151' }}>{p.label}</div>
                                                    <div style={{ fontSize: 10, color: '#9ca3af' }}>{fmtNum(p.orders)} orders</div>
                                                </div>
                                            </div>
                                            <div style={{ fontSize: 14, fontWeight: 800, color: '#111827' }}>{fmt(p.rev)}</div>
                                        </div>
                                    ))}
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 4px', borderTop: '1px dashed #e5e7eb', marginTop: 2 }}>
                                        <span style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>Total</span>
                                        <span style={{ fontSize: 14, fontWeight: 800, color: '#0ea5e9' }}>{fmt(totalOnlineSales?.totalRevenue)}</span>
                                    </div>
                                </Card>

                                <Card title="Petty Cash" icon="bi-cash-stack">
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                                        {[
                                            { label: 'Credit', value: fmt(pettyCashSummary?.totalCreditAmount), color: '#22c55e' },
                                            { label: 'Debit', value: fmt(pettyCashSummary?.totalDebitAmount), color: '#ef4444' },
                                            // { label: 'Net', value: fmt(pettyCashSummary?.netExpense), color: '#f59e0b' },
                                        ].map(c => (
                                            <div key={c.label} style={{ textAlign: 'center', padding: '8px 4px', background: '#f9fafb', borderRadius: 8 }}>
                                                <div style={{ fontSize: 9, color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase' }}>{c.label}</div>
                                                <div style={{ fontSize: 12, fontWeight: 800, color: c.color, marginTop: 2 }}>{c.value}</div>
                                            </div>
                                        ))}
                                    </div>
                                </Card>

                                <Card title="Stock Alerts" icon="bi-exclamation-triangle-fill">
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                                        <StockAlertBox count={exhaustedStockSummary?.totalExhaustedEntries} label="Exhausted Products" bg="#fff5f5" color="#ef4444" products={exhaustedStockSummary?.products} />
                                        <StockAlertBox count={damagedProductsSummary?.totalDamagedProducts} label="Damaged Products" bg="#fffbeb" color="#f59e0b" products={damagedProductsSummary?.products} />
                                    </div>
                                </Card>

                                <Card title="Damaged Raw Material" icon="bi-boxes">
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 6 }}>
                                        <StockAlertBox
                                            count={data?.rawMaterialSummary?.totalTransactions}
                                            label="Damaged Raw Material Transactions"
                                            bg="#fff5f5" color="#ef4444"
                                            products={(data?.rawMaterialSummary?.rawMaterials || []).map(r => ({
                                                productId: r.rawMaterialId,
                                                productName: r.rawMaterialName,
                                                exhaustedCount: r.quantity,
                                            }))}
                                        />
                                    </div>
                                </Card>
                            </div>

                            {/* Col B */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                <Card title="Hourly Distribution" icon="bi-clock-fill">
                                    {hourlyDistribution?.length > 0 ? (
                                        <>
                                            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                                                <span style={{ width: 110, fontSize: 9, color: '#d1d5db', textTransform: 'uppercase', fontWeight: 700 }}>Slot</span>
                                                <span style={{ flex: 1 }} />
                                                <span style={{ width: 52, fontSize: 9, color: '#d1d5db', textAlign: 'right', fontWeight: 700 }}>Orders</span>
                                                <span style={{ width: 72, fontSize: 9, color: '#d1d5db', textAlign: 'right', fontWeight: 700 }}>Sales</span>
                                            </div>
                                            {hourlyDistribution.map((h, i) => (
                                                <HourlyBar key={i} slot={h.timeSlot} orders={h.totalOrders} sales={h.netSales} maxOrders={maxOrders} />
                                            ))}
                                        </>
                                    ) : (
                                        <div style={{ textAlign: 'center', padding: '20px 0', color: '#d1d5db', fontSize: 12 }}>No hourly data</div>
                                    )}
                                </Card>

                                {topCoupons?.length > 0 && (
                                    <Card title="Top Coupons Used" icon="bi-tag-fill">
                                        {topCoupons.map((c) => (
                                            <CouponRow key={c._id} coupon={c.couponCode} used={c.totalUsed} discount={c.totalDiscount} />
                                        ))}
                                    </Card>
                                )}
                            </div>

                            {/* Col C */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                <Card title="Top Performers" icon="bi-trophy-fill">
                                    {(topProducts || []).length > 0
                                        ? topProducts.map((p, i) => <RankRow key={p._id} rank={i + 1} name={p.productName} qty={p.totalQty} isTop={true} />)
                                        : <div style={{ fontSize: 11, color: '#d1d5db', padding: '8px 0' }}>No data</div>
                                    }
                                </Card>
                                <Card title="Low Performers" icon="bi-arrow-down-circle-fill">
                                    {(bottomProducts || []).length > 0
                                        ? bottomProducts.map((p, i) => <RankRow key={p._id} rank={i + 1} name={p.productName} qty={p.totalQty} isTop={false} />)
                                        : <div style={{ fontSize: 11, color: '#d1d5db', padding: '8px 0' }}>No data</div>
                                    }
                                </Card>
                            </div>
                        </div>
                    </>
                )}

                <style>{`
                    @media (max-width: 960px) { .bento-grid { grid-template-columns: 1fr 1fr !important; } }
                    @media (max-width: 620px) { .bento-grid { grid-template-columns: 1fr !important; } }
                `}</style>
            </div>

            {/* ── Orders Modal ── */}
            <OrdersModal
                show={ordersModal.show}
                onHide={closeOrdersModal}
                modalType={ordersModal.type}
                outletId={outletId}
                dateFilter={dateFilter}
                appliedCustomRange={appliedCustomRange}
                outletName={outletName}
            />
        </React.Fragment>
    );
};

export default OutletDetailScreen;